/**
 * TNCY-6 §c — `useOwnerMemberList` reusable primitive.
 *
 * Fires BOTH cross-tenant scopes of `GET /projects` and merges them into a
 * single list (Christian 2026-06-23 — ONE table, not tabs):
 *
 *   - `scope=own`     — the caller's own projects, SERVER-paginated (the
 *                       page's real `page` / `pageSize` / `sort` / filters).
 *   - `scope=member`  — the cross-tenant fan-out of GRANTED projects, loaded
 *                       in FULL but BOUNDED (one page at `MEMBER_PAGE_SIZE`).
 *
 * v1 merge strategy (OQ-TNCY-6): member rows are bounded-but-complete, own
 * rows are server-paginated; we merge + client-sort the VISIBLE union by the
 * active sort key. A heavy-member tenant (member rows > the bound) is the
 * escape-hatch case for a future server-merged endpoint — flagged, not
 * solved here.
 *
 * The own-only path renders UNCHANGED: when there are no member rows the
 * hook returns the own query's items + its server `total` verbatim, so a
 * tenant with no cross-tenant grants sees exactly the pre-TNCY-6 list.
 *
 * Reused (unchanged) by the Contracts / Positions / IIs / Data-Acquisition
 * merged lists in TNCY-6b — keep the return shape stable.
 */
import { useMemo } from "react";
import { useProjectsListQuery } from "../useProjects.js";
import {
  mergeOwnerMember,
  MEMBER_PAGE_SIZE,
} from "../../shared/ownership/index.js";
import type {
  ListProjectsParams,
  ProjectListItem,
  ProjectsSortDir,
  ProjectsSortKey,
} from "../types.js";

// Re-export so existing `features/projects/access/useOwnerMemberList`
// importers keep their path (the bound is now defined in the shared
// merge primitive — TNCY-6b).
export { MEMBER_PAGE_SIZE };

export interface OwnerMemberListResult {
  /** The merged, client-sorted visible union for the current page. */
  rows: ProjectListItem[];
  /** Server total of OWN rows — drives the footer / pagination. Member rows
   *  are an additive, bounded overlay and do not inflate the paged total
   *  (own rows remain the server-paginated spine). */
  total: number;
  /** True if ANY member rows were merged in (drives the ownership column). */
  hasMemberRows: boolean;
  /** True when the bounded member fetch may be truncated (OQ-TNCY-6). */
  memberTruncated: boolean;
  /** PM-WF-01 — the own query's "Workflow not set" facet count (projects/v1
   *  2.25.0); undefined until loaded or against an older BE. */
  workflowUnsetCount?: number | undefined;
  isPending: boolean;
  isError: boolean;
}

function compareBy(
  key: ProjectsSortKey,
  dir: ProjectsSortDir,
): (a: ProjectListItem, b: ProjectListItem) => number {
  const sign = dir === "asc" ? 1 : -1;
  return (a, b) => {
    let av: number | string;
    let bv: number | string;
    switch (key) {
      case "projectCode":
      case "totalAssets":
      case "progressPct":
        av = a[key];
        bv = b[key];
        break;
      case "startDate":
        av = a.startDate ?? "";
        bv = b.startDate ?? "";
        break;
      case "customerName":
        av = a.customerTenantName ?? "";
        bv = b.customerTenantName ?? "";
        break;
      case "contractorName":
        av = a.contractorTenantName ?? "";
        bv = b.contractorTenantName ?? "";
        break;
      case "projectName":
        av = a.projectName;
        bv = b.projectName;
        break;
      case "state":
        av = a.state;
        bv = b.state;
        break;
      default:
        av = a.projectName;
        bv = b.projectName;
    }
    if (typeof av === "number" && typeof bv === "number") {
      return (av - bv) * sign;
    }
    return String(av).localeCompare(String(bv)) * sign;
  };
}

/**
 * @param params  the page's list params (page/pageSize/sort/filters). The
 *                hook derives the own + member queries from it; do NOT pass
 *                `scope` — it's set internally.
 */
export function useOwnerMemberList(
  params: ListProjectsParams,
): OwnerMemberListResult {
  // OWN — verbatim params, server-paginated. Explicit `scope: "own"` keeps
  // the cache key distinct from any legacy un-scoped call.
  const ownParams = useMemo<ListProjectsParams>(
    () => ({ ...params, scope: "own" }),
    [params],
  );
  const own = useProjectsListQuery(ownParams);

  // MEMBER — same filters, bounded single page, sort handled client-side on
  // the merged union so we don't depend on the BE honouring sort on the
  // fan-out path.
  const memberParams = useMemo<ListProjectsParams>(
    () => ({
      ...params,
      scope: "member",
      page: 1,
      pageSize: MEMBER_PAGE_SIZE,
    }),
    [params],
  );
  const member = useProjectsListQuery(memberParams);

  const sortKey = params.sortBy ?? "projectCode";
  const sortDir = params.sortDir ?? "desc";

  return useMemo<OwnerMemberListResult>(() => {
    // PM-WF-01 — while the workflow filter is on, the list is the owner's work
    // queue: member rows are not the caller's to set (and the BE ignores the
    // filter on scope=member), so they are left out.
    const memberItems =
      params.workflow !== undefined ? [] : (member.data?.items ?? []);
    // Member query failing is non-fatal — a member 4xx/5xx never breaks the
    // own list. Own is the spine: its pending/error drives the list; the
    // member overlay is additive and best-effort. The merge/dedupe/sort is
    // the shared `mergeOwnerMember` primitive (TNCY-6b).
    const merged = mergeOwnerMember<ProjectListItem>({
      ownItems: own.data?.items ?? [],
      ownTotal: own.data?.total ?? 0,
      memberItems,
      memberTotal: member.data?.total ?? memberItems.length,
      getId: (r) => r.projectId,
      compare: compareBy(sortKey, sortDir),
    });
    return {
      ...merged,
      workflowUnsetCount: own.data?.workflowUnsetCount,
      isPending: own.isPending,
      isError: own.isError,
    };
  }, [
    own.data,
    member.data,
    own.isPending,
    own.isError,
    sortKey,
    sortDir,
    params.workflow,
  ]);
}
